import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, combineLatest, Observable, shareReplay, switchMap, throwError } from 'rxjs';
import { AlertsApi, PagedAlertSettingsVariablesResponse } from 'src/app/hickory-api';

// This was created because there are multiple potentially unrelated components that may need this data.
// Since this data rarely changes, we only get it from the API when the first component needs it,
// and then reuse the data for any other component that might need it.
@Injectable({
  providedIn: 'root',
})
export class AvailableTemplatesService {
  private readonly cache = new Map<string, Observable<PagedAlertSettingsVariablesResponse>>();

  private readonly distributionListIdSubject = new BehaviorSubject<string>(null);
  private readonly distributionListId$ = this.distributionListIdSubject.asObservable();

  private readonly surveyInfoSubject = new BehaviorSubject<SurveyInfo>({ surveyId: null, responseId: null });
  private readonly surveyInfo$ = this.surveyInfoSubject.asObservable();
  public setDistributionListId(id: string | null) {
    this.distributionListIdSubject.next(id);
  }

  public setSurveyInfo(info: SurveyInfo) {
    this.invalidateSurveyCache(info.surveyId, info.responseId);
    this.surveyInfoSubject.next(info);
  }

  public resetParameters() {
    this.setDistributionListId(null);
    this.setSurveyInfo({ surveyId: null, responseId: null });
  }

  public  getAllParams(alertTemplateId: number): GetAvailableTemplatesRequest  {
     return {
      page: 1,
      pageSize: 200,
      alertTemplateId: alertTemplateId
    };
  }

  constructor(private readonly alertApi: AlertsApi) {}

  public getAllAvailableTemplates(alertTemplateId: number = null) {
    return this.getAvailableTemplates(alertTemplateId);
  }

  public getAllAvailableAlertTemplates(alertTemplateId: number) {
    const params: GetAvailableTemplatesRequest = {
      page: 1,
      pageSize: 200,
      type: 'Alert',
    };

    return this.getAvailableTemplates(alertTemplateId, params);
  }

  public getAvailableTemplates(
    alertTemplateId: number,
    params: GetAvailableTemplatesRequest = this.getAllParams(alertTemplateId)
  ): Observable<PagedAlertSettingsVariablesResponse> {
    // Combine distributionListId with other params to ensure reactivity
    return combineLatest([this.distributionListId$, this.surveyInfo$, new BehaviorSubject(params)]).pipe(
      switchMap(([distributionListId, surveyInfo, currentParams]) => {
        const updatedParams = { ...currentParams, surveyInfo, distributionListId, alertTemplateId };
        const key = this.getCacheKey(updatedParams);

        if (!this.cache.has(key)) {
          const request$ = this.alertApi
            .getAlertSettingsVariables(
              updatedParams.searchTerm,
              updatedParams.type,
              updatedParams.distributionListId,
              updatedParams.surveyInfo?.surveyId,
              updatedParams.surveyInfo?.responseId,
              updatedParams.page,
              updatedParams.pageSize,
              updatedParams.sort,
              updatedParams.alertTemplateId
            )
            .pipe(
              catchError((error) => {
                this.cache.delete(key);
                return throwError(() => error);
              }),
              shareReplay(1),
            );

          this.cache.set(key, request$);
        }

        return this.cache.get(key)!;
      }),
    );
  }

  public invalidateCache(): void {
    this.cache.clear();
  }

  public invalidateSurveyCache(surveyId?: string, responseId?: string): void {
    const keysToDelete: string[] = [];

    for (const [key] of this.cache.entries()) {
      try {
        const parsedKey = JSON.parse(key);

        // Type guard check
        if (!this.isCacheKeyStructure(parsedKey)) {
          continue;
        }

        if (surveyId && responseId) {
          if (parsedKey.senderSurveyId === surveyId && parsedKey.senderSurveyResponseId === responseId) {
            keysToDelete.push(key);
          }
        } else {
          // Otherwise, invalidate all survey-related entries
          if (parsedKey.senderSurveyId || parsedKey.senderSurveyResponseId) {
            keysToDelete.push(key);
          }
        }
      } catch (error) {
        // If JSON.parse fails, skip this key
        console.warn('Failed to parse cache key:', key, error);
      }
    }

    keysToDelete.forEach((key) => this.cache.delete(key));
  }

  private getCacheKey(params: GetAvailableTemplatesRequest): string {
    const keyStructure: CacheKeyStructure = {
      page: params.page ?? null,
      pageSize: params.pageSize ?? null,
      sort: params.sort ?? null,
      searchTerm: params.searchTerm ?? '',
      type: params.type,
      distributionListId: params.distributionListId ?? null,
      senderSurveyId: params.surveyInfo?.surveyId ?? null,
      senderSurveyResponseId: params.surveyInfo?.responseId ?? null,
      alertTemplateId: params.alertTemplateId ?? null
    };

    return JSON.stringify(keyStructure);
  }

  private isCacheKeyStructure(obj: unknown): obj is CacheKeyStructure {
    return obj && typeof obj === 'object' && 'senderSurveyId' in obj && 'senderSurveyResponseId' in obj;
  }
}

export interface GetAvailableTemplatesRequest {
  page?: number;
  pageSize?: number;
  sort?: string;
  searchTerm?: string;
  type?: 'Alert' | 'Contact' | 'Survey' | 'CSV';
  distributionListId?: string;
  surveyInfo?: SurveyInfo;
  alertTemplateId?: number;
}

interface CacheKeyStructure {
  page: number | null;
  pageSize: number | null;
  sort: string | null;
  searchTerm: string;
  type?: 'Alert' | 'Contact' | 'Survey' | 'CSV';
  distributionListId: string | null;
  senderSurveyId: string | null;
  senderSurveyResponseId: string | null;
  alertTemplateId?: number;
}

interface SurveyInfo {
  surveyId?: string;
  responseId?: string;
}
